import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from "react-hot-toast";
import { clearUser, updateProfile } from '../../features/auth/authSlice';
import apiClient from '../../services/apiClient';
import Breadcrumbs from "../breadcrumbs/Breadcrumbs";
import { PiUser } from "react-icons/pi";
import { MdOutlineMoveToInbox } from "react-icons/md";
import { IoSettingsOutline } from "react-icons/io5";
import { IoIosArrowBack } from "react-icons/io";
import { RiLogoutCircleRLine } from "react-icons/ri";
import { Link } from "react-router-dom";
import { CiCamera } from "react-icons/ci";
import defaultUser from '../../assets/images/user.jpg';
import Pagination from '../pagination/Pagination';
import OrderTraking from '../order/OrderTraking';
import { IoEyeOutline, IoEyeOffOutline } from "react-icons/io5";
import { IMAGE_PATH } from '../../utils/constants';

const Profile = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  // console.log(user)

  const [activeMenu, setActiveMenu] = useState('profile');
  const [showMobileContent, setShowMobileContent] = useState(false);
  const [profileImage, setProfileImage] = useState(defaultUser);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [photo, setPhoto] = useState(null);
  const [orderItems,setOrderItes] = useState([]);
  const [viewOrderItems,setViewingOrderItems] = useState([]);

  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    secondary_phone: user?.secondary_phone || '',
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);

  const [passwordStates, setPasswordStates] = useState({
    old: false,
    new: false,
    confirm: false
  });

  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 5;

  

  useEffect(() => {
    if (user?.photo) {
      // console.log(user.photo)
      setProfileImage(`${IMAGE_PATH}${user.photo}`);
    }
  }, [user]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setShowMobileContent(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleUpdateProfile = async (formData) => {
    setIsLoading(true);
    try {
      const response = await apiClient.post("/user/profile/update", formData);
      // console.log(response.data.user)
      dispatch(
        updateProfile(response.data.user)
      );
      setIsLoading(false);
      toast.success(response.data.message);
      setPhoto(null); // Reset photo state after successful upload
    } catch (error) {
      console.log(error)
      toast.error(`Error updating user: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };


  const fetchOrders = async () => {
    
    const response = await apiClient.get('/orders',{
      params :{
        user_id: user.id,
        all: true,
        role: 0,
      }  
    })
    setOrderItes(response.data.data)

    // console.log(response)
  }

  useEffect(()=>{
    fetchOrders();
  },[])

  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = orderItems.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(orderItems.length / ordersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (["new_password"].includes(name)) {
      setPasswordStrength(checkPasswordStrength(value));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Trimmed values for validation
    const name = formData.name.trim();
    const phone = formData.phone.trim();
    const email = formData.email.trim();
    const secondaryPhone = formData.secondary_phone.trim();

    // Validation
    if (!name) {
      toast.error("Name is required");
      return;
    }

    if (!phone) {
      toast.error("Primary phone number is required");
      return;
    }

    if (!/^\d{10}$/.test(phone)) {
      toast.error("Primary phone number must be 10 digits");
      return;
    }

    if (secondaryPhone && !/^\d{10}$/.test(secondaryPhone)) {
      toast.error("Secondary phone number must be 10 digits");
      return;
    }

    if (secondaryPhone === phone) {
      toast.error("Secondary phone number cannot be same as Primary phone number");
      return;
    }

    if (!email) {
      toast.error("Email is required");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Invalid email address");
      return;
    }

    if (photo && !photo.type.startsWith("image/")) {
      toast.error("Uploaded file must be an image");
      return;
    }

    // If all validations pass, append to FormData
    const payload = new FormData();
    payload.append("name", name);
    payload.append("phone", phone);
    payload.append("email", email);
    payload.append("secondary_phone", secondaryPhone);
    if (photo) payload.append("photo", photo);

    handleUpdateProfile(payload);

  };


  const breadcrumbItems = [
    { label: "Home", url: "/" },
    { label: "Profile" },
  ];

  const handleLogout = () => {
    dispatch(clearUser());
  };

  const handleCameraClick = () => {
    fileInputRef.current.click();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.match('image.*')) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onload = (event) => setProfileImage(event.target.result);
      reader.readAsDataURL(file);
    } else {
      toast.error('Please select a valid image file');
    }
  };

  const handleViewOrder = (order,item) => {
    setViewingOrder(order);
    setViewingOrderItems(item);
    if (isMobile) setShowMobileContent(true);
  };

  const handleBackToOrders = () => {
    setViewingOrder(null);
  };

  const togglePassword = (field) => {
    setPasswordStates(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleMenuClick = (menu) => {
    setActiveMenu(menu);
    if (isMobile) setShowMobileContent(true);
  };

  const handleBackToMenu = () => {
    setShowMobileContent(false);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.current_password) {
      newErrors.current_password = "Current password is required";
    }
    
    if (!formData.new_password) {
      newErrors.new_password = "New password is required";
    } else if (formData.new_password.length < 8) {
      newErrors.new_password = "Password must be at least 8 characters long";
    } else if (passwordStrength < 3) {
      newErrors.new_password = "Password is too weak. Include uppercase, lowercase, numbers, and special characters";
    }
    
    if (!formData.confirm_password) {
      newErrors.confirm_password = "Please confirm your new password";
    } else if (formData.new_password !== formData.confirm_password) {
      newErrors.confirm_password = "Passwords do not match";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitPass = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      await apiClient.post("/change-password", {
        current_password: formData.current_password,
        new_password: formData.new_password,
        new_password_confirmation: formData.confirm_password
      });
      
      toast.success("Password changed successfully!");
      
      // Reset form
      setFormData({
        current_password: "",
        new_password: "",
        confirm_password: ""
      });
      setPasswordStrength(0);
      
    } catch (error) {
      console.error(error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.data?.errors) {
        // Handle validation errors from server
        const serverErrors = error.response.data.errors;
        setErrors(serverErrors);
        toast.error("Please fix the errors in the form");
      } else {
        toast.error("Failed to change password");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Password strength checker (simple)
  function checkPasswordStrength(password) {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  }

  return (
    <div id='Profile'>
      <div className="BreadCrumbsBox">
        <div className="container">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>
      <div className='container'>
        <div className='ProfileMain'>
          <div className={`ProfileMainBox ${showMobileContent ? 'show-content' : ''}`}>
        
            <div className={`ProfileLeftBox ${showMobileContent ? 'hidden' : ''}`}>
              <div className='ProfileTopBox'>
                <div className='UserImgBox'>
                  <img src={profileImage} alt="User profile" />
                </div>
                <div className='UserNameBox'>
                  <p>{user?.name || 'User'}</p>
                </div>
              </div>
              
              <div className='ProfileMidleBox'>
                <div 
                  className={`ProfileMenuBox ${activeMenu === 'profile' ? 'active' : ''}`}
                  onClick={() => handleMenuClick('profile')}
                >
                  <div className='ProfileMenuIconBox'>
                    <PiUser />
                  </div>
                  <div className='ProfileMenuTextBox'>
                    <p>Profile</p>
                  </div>
                </div>
                
                <div 
                  className={`ProfileMenuBox ${activeMenu === 'orders' ? 'active' : ''}`}
                  onClick={() => handleMenuClick('orders')}
                >
                  <div className='ProfileMenuIconBox'>
                    <MdOutlineMoveToInbox />
                  </div>
                  <div className='ProfileMenuTextBox'>
                    <p>My Orders</p>
                  </div>
                </div>
                
                <div 
                  className={`ProfileMenuBox ${activeMenu === 'settings' ? 'active' : ''}`}
                  onClick={() => handleMenuClick('settings')}
                >
                  <div className='ProfileMenuIconBox'>
                    <IoSettingsOutline />
                  </div>
                  <div className='ProfileMenuTextBox'>
                    <p>Settings</p>
                  </div>
                </div>
                
                <Link to="/" className='ProfileMenuBox' onClick={handleLogout}>
                  <div className='ProfileMenuIconBox'>
                    <RiLogoutCircleRLine />
                  </div>
                  <div className='ProfileMenuTextBox'>
                    <p>Logout</p>
                  </div>
                </Link>
              </div>
            </div>
            
            <div className={`ProfileRightBox ${showMobileContent ? 'active' : ''}`}>
              {isMobile && (
                <div className='BackButton' onClick={handleBackToMenu}>
                  <IoIosArrowBack /> Back to Menu
                </div>
              )}
              
              {activeMenu === 'profile' && (
                <div className='ProfileInfoBox'>
                  <div className='ProfileInfoHead'>
                    <p>Profile Information</p>
                  </div>

                  <div className='InfoBox'>
                    <div className='UserImgBox'>
                      <img src={profileImage} alt="User profile" />
                      <div className='CameraIconBox' onClick={handleCameraClick}>
                        <CiCamera />
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleImageUpload}
                          accept="image/*"
                          style={{ display: 'none' }}
                        />
                      </div>
                    </div>
                    
                    <div className='FormGroup'>
                      <form onSubmit={handleSubmit}>
                        <div className='InnerFormGroup'>
                          <div className='FormField'>
                            <label>Full Name</label>
                            <input 
                              type="text" 
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              placeholder="Enter your name"
                            />
                          </div>
                        </div>
                        
                        <div className='InnerFormGroup'>
                          <div className='FormField'>
                            <label>Email</label>
                            <input 
                              type="email" 
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              placeholder="Enter your email"
                              readOnly
                            />
                          </div>
                        </div>
                        <div className='InnerFormGroup'>
                          <div className='FormField'>
                            <label>Phone Number</label>
                            <input 
                              type="tel" 
                              name="phone"
                              value={formData.phone}
                              onChange={handleInputChange}
                              placeholder="Enter your phone number"
                            />
                          </div>
                        </div>
                        
                        <div className='InnerFormGroup'>
                          <div className='FormField'>
                            <label>Secondary Phone Number</label>
                            <input 
                              type="tel" 
                              name="secondary_phone"
                              value={formData.secondary_phone}
                              onChange={handleInputChange}
                              placeholder="Enter secondary phone number"
                            />
                          </div>
                        </div>
                        <div className='FormSubmitBtn'>
                          <button 
                            type="submit" 
                            disabled={isLoading}
                          >
                            {isLoading ? 'Saving...' : 'Save Changes'}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}
              
             {activeMenu === 'orders' && (
                viewingOrder ? (
                  <OrderTraking 
                    order={viewingOrder} 
                    orderItems={viewOrderItems}
                    onBack={handleBackToOrders} 
                  />
                ) : (
                  <div className="OrdersListBox">
                    <div className="MyOrdersHead">
                      <p>My Orders</p>
                    </div>
                    <form>
                      <table className="OrderTable">
                        <thead>
                          <tr>
                            <th className="TableHead">Order</th>
                            <th className="TableHead">Product</th>
                            <th className="TableHead">Price</th>
                            <th className="TableHead">Status</th>
                            <th className="TableHead">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentOrders.map((order, index) => {
                            const firstItem = order.order_items?.[0];
                            const remainingCount = (order.order_items?.length || 0) - 1;

                            return (
                              <tr className="ProductRow" key={`${order.id}-${index}`}>
                                <td className="OrderId">
                                  <span>#{order.id}</span>
                                </td>

                                {/* Product Column */}
                                <td>
                                  {firstItem ? (
                                    <div className="ProductImgTitleBox">
                                      <a
                                        href={firstItem.product_type === "combo" ? `/combo/${firstItem.combo?.id}` : `/products/${firstItem.product?.id}`}
                                        className="ProductImg"
                                      >
                                        <img
                                          src={
                                            firstItem.product_type === "normal"
                                              ? IMAGE_PATH + firstItem.product?.media?.[0]?.file_path
                                              : IMAGE_PATH + firstItem.combo?.image_path
                                          }
                                          alt={
                                            firstItem.product_type === "normal"
                                              ? firstItem.product?.name
                                              : firstItem.combo?.title
                                          }
                                          onError={(e) => {
                                            e.currentTarget.src = "/images/placeholder-product.jpg";
                                          }}
                                        />
                                      </a>
                                      <div className="ProductTitleSizeBox">
                                        <p className="ProductTitle">
                                          <a
                                            href={`/products/productdetails/${firstItem.product?.id || firstItem.combo?.id}`}
                                          >
                                            {firstItem.product_type === "normal"
                                              ? firstItem.product?.name
                                              : firstItem.combo?.title}
                                          </a>
                                        </p>
                                        <div className="ProductSize">
                                          <p>
                                            Size:{" "}
                                            {firstItem.product?.size ||
                                              firstItem.combo?.products?.[0]?.size ||
                                              "N/A"}
                                          </p>
                                        </div>

                                        {/* Show +N products if more exist */}
                                        {remainingCount > 0 && (
                                          <p className="MoreProducts">+ {remainingCount} products</p>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    // Fallback if no items
                                    <div className="ProductImgTitleBox">
                                      <div className="ProductImg">
                                        <img src="/images/placeholder-product.jpg" alt="Unknown Product" />
                                      </div>
                                      <div className="ProductTitleSizeBox">
                                        <p className="ProductTitle">Order #{order.id}</p>
                                        <div className="ProductSize">
                                          <p>No product details available</p>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </td>

                                {/* Price Column */}
                                <td className="Price">₹{  parseFloat(order.total) + parseFloat(order.shipping_fee) }</td>

                                {/* Status Column */}
                                <td>
                                  <div className={`OrderStatus ${order.status.toLowerCase()}`}>
                                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                  </div>
                                </td>

                                {/* Action Column */}
                                <td>
                                  <button
                                    className="OrderAction"
                                    onClick={() => handleViewOrder(order)}
                                  >
                                    View
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>

                      </table>
                    </form>

                    {totalPages > 1 && (
                      <div className="PaginationWrapper">
                        <Pagination 
                          currentPage={currentPage} 
                          totalPages={totalPages} 
                          onPageChange={paginate} 
                        />
                      </div>
                    )}
                  </div>
                )
              )}
              
              {activeMenu === 'settings' && (
                <div className="SettingsBox">
                  <div className='SettingHead'>
                    <p>Password Change</p>
                  </div>
                  <div className='PasswordBox'>
                    <div className='FormGroup'>
                      <form onSubmit={handleSubmitPass}>
                        <div className='PasswordFieldBox'>
                          <label>Old Password</label>
                          <div className="PasswordInputWrapper">
                            <input 
                              type={passwordStates.old ? "text" : "password"} 
                              name="current_password"
                              value={formData.current_password}
                              onChange={handleInputChange}
                              placeholder="Enter old password"
                            />
                            <button 
                              type="button" 
                              className="TogglePassword"
                              onClick={() => togglePassword('old')}
                            >
                              {passwordStates.old ? <IoEyeOutline /> : <IoEyeOffOutline />}
                            </button>
                          </div>
                          {errors.current_password && <div className="ErrorMsg">{errors.current_password}</div>}
                        </div>

                        <div className='PasswordFieldBox'>
                          <label>New Password</label>
                          <div className="PasswordInputWrapper">
                            <input 
                              type={passwordStates.new ? "text" : "password"} 
                              name="new_password"
                              value={formData.new_password}
                              onChange={handleInputChange}
                              placeholder="Enter new password"
                            />
                            <button 
                              type="button" 
                              className="TogglePassword"
                              onClick={() => togglePassword('new')}
                            >
                              {passwordStates.new ? <IoEyeOutline /> : <IoEyeOffOutline />}
                            </button>
                          </div>
                          {errors.new_password && <div className="ErrorMsg">{errors.new_password}</div>}
                        </div>

                        <div className='PasswordFieldBox'>
                          <label>Confirm Password</label>
                          <div className="PasswordInputWrapper">
                            <input 
                              type={passwordStates.confirm ? "text" : "password"} 
                              name="confirm_password"
                              value={formData.confirm_password}
                              onChange={handleInputChange}
                              placeholder="Confirm new password"
                            />
                            <button 
                              type="button" 
                              className="TogglePassword"
                              onClick={() => togglePassword('confirm')}
                            >
                              {passwordStates.confirm ? <IoEyeOutline /> : <IoEyeOffOutline />}
                            </button>
                          </div>
                          {errors.confirm_password && <div className="ErrorMsg">{errors.confirm_password}</div>}
                        </div>

                        <div className="SaveChangesButtonBox">
                          <button type="submit" className="SaveChangesButton">
                            Save Changes
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;